import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Heart } from "lucide-react";

// Logo-inspired hero animation: a sage Wonder Parcel parcel gently settles, opens,
// a heart rises, and champagne-gold sparkles appear — then it rests (matching the logo).
// One-shot intro (no bouncing / no looping confetti). Respects prefers-reduced-motion.
export const GiftBox = ({ className = "" }) => {
  const reduce = useReducedMotion();
  const ease = [0.22, 1, 0.36, 1];

  // Resting (final) state is the open parcel with the heart above — like the logo.
  const box = reduce
    ? { initial: { opacity: 1, y: 0, scale: 1 }, animate: { opacity: 1, y: 0, scale: 1 } }
    : { initial: { opacity: 0, y: 24, scale: 0.9 }, animate: { opacity: 1, y: 0, scale: 1 } };
  const lid = reduce
    ? { initial: { y: -26, rotate: -8 }, animate: { y: -26, rotate: -8 } }
    : { initial: { y: 0, rotate: 0 }, animate: { y: -26, rotate: -8 } };
  const heart = reduce
    ? { initial: { opacity: 1, y: -18, scale: 1 }, animate: { opacity: 1, y: -18, scale: 1 } }
    : { initial: { opacity: 0, y: 44, scale: 0.5 }, animate: { opacity: 1, y: -18, scale: 1 } };

  return (
    <div className={`relative ${className}`} aria-hidden="true">
      <div className="relative mx-auto" style={{ width: 300, height: 300 }}>
        {/* soft ground shadow */}
        <div className="absolute left-1/2 bottom-3 h-6 w-44 -translate-x-1/2 rounded-full bg-[hsl(var(--wp-shadow-warm)/0.45)] blur-2xl" />

        {/* champagne-gold rays behind the heart (like the logo) */}
        {!reduce && (
          <motion.svg className="absolute left-1/2 top-8 -translate-x-1/2" width="150" height="90" viewBox="0 0 150 90"
            initial={{ opacity: 0 }} animate={{ opacity: 0.9 }} transition={{ delay: 1.15, duration: 0.7 }}>
            {[30, 55, 95, 120].map((x, i) => (
              <line key={i} x1="75" y1="82" x2={x} y2={i === 1 || i === 2 ? 18 : 30} stroke="hsl(var(--wp-gold))" strokeWidth="2.5" strokeLinecap="round" opacity="0.7" />
            ))}
          </motion.svg>
        )}

        {/* heart rising from the parcel */}
        <motion.div className="absolute left-1/2 top-10 z-30 -translate-x-1/2"
          initial={heart.initial} animate={heart.animate}
          transition={{ delay: 1.0, duration: 0.85, ease }}>
          <Heart className="h-14 w-14 fill-wp-rose text-wp-rose-strong drop-shadow-sm" />
        </motion.div>

        {/* box body (sage → forest, like the logo box) */}
        <motion.div initial={box.initial} animate={box.animate} transition={{ duration: 0.6, ease }}
          className="absolute bottom-10 left-1/2 h-40 w-56 -translate-x-1/2 rounded-b-[18px] rounded-t-[8px] bg-gradient-to-b from-wp-rose to-wp-rose-strong shadow-wp-card">
          {/* cream inner flaps */}
          <div className="absolute -top-3 left-3 h-10 w-24 origin-bottom-left -rotate-12 rounded-md bg-wp-cream-100 shadow-sm" />
          <div className="absolute -top-3 right-3 h-10 w-24 origin-bottom-right rotate-12 rounded-md bg-wp-cream-100 shadow-sm" />
          {/* WP monogram hint */}
          <span className="font-display absolute bottom-6 left-1/2 -translate-x-1/2 text-3xl italic text-white/90">WP</span>
        </motion.div>

        {/* lid (opens up like the logo's open parcel) */}
        <motion.div initial={lid.initial} animate={lid.animate} transition={{ delay: 0.55, duration: 0.6, ease }}
          className="absolute bottom-[120px] left-1/2 z-20 h-14 w-64 origin-bottom -translate-x-1/2 rounded-[12px] bg-gradient-to-b from-white to-wp-cream-100 shadow-wp-lift">
          <div className="absolute left-1/2 top-0 h-full w-9 -translate-x-1/2 bg-wp-rose-strong/85" />
        </motion.div>

        {/* champagne-gold sparkles — appear, then twinkle very gently */}
        {[[26, 40, 0], [252, 66, 0.25], [46, 210, 0.5], [258, 196, 0.15], [140, 12, 0.4]].map(([x, y, d], i) => (
          <motion.span key={i} className="absolute text-wp-gold" style={{ left: x, top: y, fontSize: 18 }}
            initial={{ opacity: 0, scale: 0.4 }}
            animate={reduce ? { opacity: 0.85, scale: 1 } : { opacity: [0, 1, 0.6, 0.9], scale: [0.4, 1.1, 0.9, 1] }}
            transition={reduce ? { duration: 0 } : { delay: 1.2 + d, duration: 3, repeat: Infinity, repeatDelay: 1.5, ease: "easeInOut" }}>
            ✦
          </motion.span>
        ))}
      </div>
    </div>
  );
};
